import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import produce from 'immer';
import { inject, Observer, observer } from 'mobx-react';
import * as React from 'react';
import {
	DragDropContext,
	Draggable,
	DraggableProvided,
	DraggingStyle,
	Droppable,
	DroppableProvided,
	DropResult,
	NotDraggingStyle,
} from 'react-beautiful-dnd';
import { IFileAttachment, SocialMediaPostViewModel } from '../../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getFileSizeStringValue } from '../../../../models/UiUtils';
import { AddMediaEmptyState } from '../../../components-v2/AddMediaEmptyState';
import { AiButton } from '../../../components-v2/AiButton';
import { Button } from '../../../components-v2/Button';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { TabValue } from '../../../components-v2/UnifiedImageDialog/types';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { white } from '../../../styles/colors';
import { DeprecatedCloseButton } from '../../DeprecatedCloseButton';
import { DragGripperIcon } from '../../svgs/icons/DragGripperIcon';
import { EnlargeIcon } from '../../svgs/icons/EnlargeIcon';
import { SparklesIcon } from '../../svgs/icons/SparklesIcon';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	className?: string;
	onClickEnlargeImage?(initialImageUrl: string): void;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	onValidateSelectedFile?: (file: File) => boolean;
	post?: SocialMediaPostViewModel;
	hasVideo: boolean;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

const MAX_IMAGES = 10;

const SocialMediaImageSearchAndDisplayBase: React.FC<IProps> = observer(props => {
	const {
		className,
		readOnly,
		hasVideo,
		onClickEnlargeImage,
		onPostScheduled,
		onValidateSelectedFile,
		post,
		impersonationContext,
	} = props;
	const { postImages, setImages, postContent, schedulePost, selectedCreator, updatePost } =
		useEditSocialMediaPostContext();
	const userSession = useUserSession();
	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<TabValue>('upload');
	const [unifiedDialogImage, setUnifiedDialogImage] = React.useState<IFileAttachment | null>(null);

	const onRemoveClicked = React.useCallback(
		(image: IFileAttachment) => () => {
			setImages(val => val?.filter(x => x.id !== image.id));
		},
		[setImages]
	);

	const handleOpenImageModal = React.useCallback((tab: TabValue = 'upload') => {
		setUnifiedDialogTab(tab);
		setUnifiedDialogImage(null);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenAIEdit = React.useCallback((image: IFileAttachment) => {
		setUnifiedDialogTab('ai-edit');
		setUnifiedDialogImage(image);
		setUnifiedDialogOpen(true);
	}, []);

	const handleUnifiedImageSelected = React.useCallback(
		(image: IFileAttachment) => {
			setImages(prev => {
				// If we're editing an existing image, replace it instead of adding a new one
				if (unifiedDialogImage) {
					return (prev || []).map(img => (img.id === unifiedDialogImage.id ? image : img));
				}
				// Otherwise, add the new image
				return [...(prev || []), image];
			});
		},
		[setImages, unifiedDialogImage]
	);

	const handleMultipleImagesSelected = React.useCallback(
		(images: Api.IFileAttachment[]) => {
			setImages(prev => {
				const combined = [...(prev || []), ...images];
				// Enforce max limit on the combined result
				if (combined.length > MAX_IMAGES) {
					return combined.slice(0, MAX_IMAGES);
				}
				return combined;
			});
		},
		[setImages]
	);

	const handleBulkRebrandingImagesSelected = (attachments: Api.IFileAttachment[]) => {
		setUnifiedDialogOpen(false);
		setImages(attachments);
	};

	const handleVideoAttachmentGenerated = React.useCallback(
		(attachment: IFileAttachment) => {
			setImages(prev => [...(prev || []), attachment]);
			setUnifiedDialogOpen(false);
		},
		[setImages]
	);

	const getPostIdForVideoUpload = React.useCallback(async (): Promise<string> => {
		if (post?.id) {
			await updatePost(post);
			return post.id;
		}
		const [postViewModel] = await schedulePost(null, null, impersonationContext);
		onPostScheduled?.(postViewModel);
		return postViewModel.id;
	}, [onPostScheduled, post, schedulePost, updatePost, impersonationContext]);

	const videoForUserId = React.useMemo(() => {
		if (impersonationContext?.isValid && !selectedCreator) {
			return undefined;
		}

		if (impersonationContext?.isValid) {
			return selectedCreator?.id;
		}

		if (selectedCreator?.id && selectedCreator.id !== userSession?.user?.id) {
			return selectedCreator.id;
		}

		return undefined;
	}, [impersonationContext?.isValid, selectedCreator, userSession?.user?.id]);

	const onDragEnd = (result: DropResult) => {
		// do nothing if the user dropped the item outside the list
		if (result.destination == null) {
			return;
		}
		const toIndex = result.destination.index;
		const fromIndex = result.source.index;
		const imageToMove = postImages[fromIndex];
		setImages(
			produce(postImages, draftImages => {
				draftImages.splice(fromIndex, 1);
				draftImages.splice(toIndex, 0, imageToMove);
			})
		);
	};

	const disabledUploadTabs = React.useMemo(() => {
		const disabledTabs: TabValue[] = [];
		const disabledTabsReason: Partial<Record<TabValue, string>> = {};

		if (hasVideo) {
			disabledTabs.push('upload-video');
			disabledTabsReason['upload-video'] = 'You can only upload one video at a time';
		}

		if (impersonationContext?.isValid && !videoForUserId) {
			disabledTabs.push('upload-video');
			disabledTabsReason['upload-video'] = 'You must select a creator to upload a video';
		}

		return { disabledTabs, disabledTabsReason };
	}, [hasVideo, impersonationContext, videoForUserId]);

	const hasMaximumAllowedImages = (postImages?.length ?? 0) >= MAX_IMAGES;

	const onRenderContent = (image: IFileAttachment) => {
		if (image.mimeType?.startsWith('video/')) {
			return (
				<div className={className ? className : css(styleSheet.imageDisplayArea)}>
					<figure className={css(styleSheet.imageDisplayAreaImageContainer)}>
						<video src={image.url} width={200} controls />

						<figcaption className={css(styleSheet.imageDisplayAreaInfo)}>
							<div>
								<span>File size:&nbsp;</span>
								{getFileSizeStringValue(image.fileSize)}
							</div>
						</figcaption>
					</figure>

					{/* This is where to insert other info about the content */}
					{!readOnly ? (
						<DeprecatedCloseButton className={css(styleSheet.imageDisplayAreaClose)} onClick={onRemoveClicked(image)} />
					) : null}
				</div>
			);
		}
		return (
			<div className={className ? className : css(styleSheet.imageDisplayArea)}>
				<div className={css(styleSheet.imageDisplayAreaImageContainer)}>
					<button className={css(styleSheet.enlargeImageButton)} onClick={() => onClickEnlargeImage(image.url)}>
						<img src={image.url} width={200} />
						<div className={`${css(styleSheet.enlargeImageText)} enlarge-image-text`}>
							<EnlargeIcon fill={white} />
							Enlarge Image{postImages?.length > 1 ? 's' : ''}
						</div>
					</button>
					<div className={css(styleSheet.imageDisplayAreaInfo)}>
						<div>
							<span>File size:&nbsp;</span>
							{getFileSizeStringValue(image.fileSize)}
						</div>
						<div className={css(styleSheet.imageActions)}>
							{!readOnly && !image.mimeType?.toLowerCase().includes('gif') && (
								<div>
									<Button size='sm' variant='secondary' onClick={() => handleOpenAIEdit(image)}>
										<SparklesIcon />
										Edit Image with AI
									</Button>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* This is where to insert other info about the content */}
				{!readOnly ? (
					<DeprecatedCloseButton className={css(styleSheet.imageDisplayAreaClose)} onClick={onRemoveClicked(image)} />
				) : null}
			</div>
		);
	};

	const getItemStyle = (draggableStyles: DraggingStyle | NotDraggingStyle) => {
		return { background: '#fff', ...draggableStyles };
	};
	return (
		<>
			{!readOnly && !hasVideo ? (
				<>
					{!postImages?.length ? (
						<AddMediaEmptyState
							title='No media selected'
							subtitle='Add images or videos to display with your post'
							onAddMedia={() => handleOpenImageModal('upload')}
						/>
					) : (
						<div className={css(styleSheet.addMediaActionsRow)}>
							<Button
								size='sm'
								variant='secondary'
								onClick={() => handleOpenImageModal('upload')}
								disabled={hasMaximumAllowedImages}
								title={hasMaximumAllowedImages ? `Only ${MAX_IMAGES} images are supported at this time` : undefined}
							>
								Add media
							</Button>
							<AiButton
								title='Apply my Branding'
								onClick={() => handleOpenImageModal('bulk-rebranding')}
								disabled={hasMaximumAllowedImages}
								size='sm'
							/>
						</div>
					)}
				</>
			) : null}
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable direction='vertical' droppableId='social-post' type='image'>
					{(droppableProvided: DroppableProvided) => {
						return (
							<Observer>
								{() => {
									return (
										<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
											{postImages ? (
												<div className={css(styleSheet.imagesContainer)}>
													{postImages.map((image, idx) => {
														return !readOnly ? (
															<Draggable
																disableInteractiveElementBlocking={true}
																draggableId={image.id}
																index={idx}
																key={image.id}
															>
																{(draggableProvided: DraggableProvided) => {
																	return (
																		<div
																			className={css(styleSheet.imageContainer)}
																			key={image.id}
																			ref={draggableProvided ? draggableProvided.innerRef : undefined}
																			{...(draggableProvided?.draggableProps || {})}
																			style={getItemStyle(draggableProvided.draggableProps?.style)}
																		>
																			<div className={css(styleSheet.gripperCol)}>
																				<span {...(draggableProvided?.dragHandleProps || {})}>
																					<DragGripperIcon />
																				</span>
																			</div>
																			{onRenderContent(image)}
																		</div>
																	);
																}}
															</Draggable>
														) : (
															<div className={css(styleSheet.imageContainer)} key={image.id}>
																{onRenderContent(image)}
															</div>
														);
													})}
												</div>
											) : null}
										</div>
									);
								}}
							</Observer>
						);
					}}
				</Droppable>
			</DragDropContext>

			<UnifiedImageDialog
				open={unifiedDialogOpen}
				onOpenChange={setUnifiedDialogOpen}
				initialTab={unifiedDialogTab}
				initialImage={unifiedDialogImage || undefined}
				onImageSelected={handleUnifiedImageSelected}
				onVideoAttachmentGenerated={handleVideoAttachmentGenerated}
				impersonationContext={impersonationContext}
				postContent={postContent}
				postId={post?.id}
				getPostIdForVideoUpload={getPostIdForVideoUpload}
				forUserId={videoForUserId}
				showUploadTab={true}
				showUploadVideoTab={!hasVideo}
				resizeForSocialMedia={true}
				onValidateSelectedFile={onValidateSelectedFile}
				bulkRebrandingImages={postImages}
				onBulkRebrandingImagesSelected={handleBulkRebrandingImagesSelected}
				maxUploadImages={MAX_IMAGES}
				allowMultipleUpload={true}
				onMultipleImagesSelected={handleMultipleImagesSelected}
				disabledTabs={disabledUploadTabs.disabledTabs}
				disabledTabsReason={disabledUploadTabs.disabledTabsReason}
			/>
		</>
	);
});

export const SocialMediaImageSearchAndDisplay = inject(ImpersonationContextKey)(SocialMediaImageSearchAndDisplayBase);
